import { createRoute, lazyRouteComponent, stripSearchParams } from '@tanstack/react-router'
import type { PitchSort } from '@volee/api-client'

import { requireBackoffice } from '../auth/guard.ts'
import { parseOneOf, parsePage, parseQuery } from './params.ts'
import { rootRoute } from './root.tsx'

// The protected campi list. Search, sort and page are typed search params (§4.4): a
// filtered view is a link, and the back button returns to the previous result.

export interface PitchSearch {
  /** 1-based: it is the number a person shares. */
  readonly page: number
  readonly q: string | null
  readonly sort: PitchSort
}

/** The orderings the API accepts. No status filter: a campo has no enum to filter on. */
const SORTS: readonly PitchSort[] = ['name', '-name', 'created_at', '-created_at']

export const DEFAULT_PITCH_SORT: PitchSort = 'name'

/** The list with no filters. `validateSearch` returns all three fields, so every link to
 * `/pitches` must declare them: this constant is what "back to the list" means. */
export const DEFAULT_PITCH_SEARCH: PitchSearch = {
  page: 1,
  q: null,
  sort: DEFAULT_PITCH_SORT,
}

export function parsePitchSort(value: unknown): PitchSort {
  return parseOneOf(SORTS, value) ?? DEFAULT_PITCH_SORT
}

/** The list as a link target: path and the search params its type requires, in one place. */
export const pitchListLink = { to: '/pitches', search: DEFAULT_PITCH_SEARCH } as const

export const pitchesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pitches',
  validateSearch: (search: Record<string, unknown>): PitchSearch => ({
    page: parsePage(search.page),
    q: parseQuery(search.q),
    sort: parsePitchSort(search.sort),
  }),
  search: {
    // Defaults leave the URL: a colleague receives /pitches?q=rivoli, not the full set.
    middlewares: [stripSearchParams(DEFAULT_PITCH_SEARCH)],
  },
  // Runs before the component is fetched: without the role, no chunk downloads.
  beforeLoad: requireBackoffice,
  component: lazyRouteComponent(() => import('../pages/pitches.tsx')),
})
